import {
  EuiFlexGroup,
  EuiSuperSelect,
  EuiSuperSelectOption,
  EuiToken,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { DataTableColumnDefinition } from "pages/gdt/store/DataTableProvider.types";
import { COLUMN_KIND_MAP } from "pages/gdt/modals/ModalCreateColumn/ModalCreateColumn";

interface FieldColumnCompressedProps {
  readOnly?: boolean;
  noItemsMessage?: string;
  // Suggested columns appear before all other columns
  isColumnSuggested: (definition: DataTableColumnDefinition) => boolean;
  tableId: string | undefined;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
}

export const FieldColumnCompressed = ({
  readOnly = false,
  noItemsMessage = "No columns found",
  isColumnSuggested,
  tableId,
  value,
  onChange,
  disabled = false,
}: FieldColumnCompressedProps) => {
  const columns = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    return [...(table?.columns ?? [])];
  });

  // Disabled option displayed when tables exist
  const noItemsOption = {
    value: "no_items",
    disabled: true,
    inputDisplay: noItemsMessage,
  };

  const options: EuiSuperSelectOption<string>[] = columns
    .sort((a, b) => {
      const isSuggestedA = isColumnSuggested(a.definition);
      const isSuggestedB = isColumnSuggested(b.definition);
      if (isSuggestedA === isSuggestedB) {
        return 0;
      } else if (isSuggestedA) {
        return -1;
      } else {
        return 1;
      }
    })
    .flatMap((column, idx, arr) => {
      let label: EuiSuperSelectOption<string> | undefined;
      const prevColumn = idx > 0 ? arr[idx - 1] : undefined;

      if (idx === 0 && isColumnSuggested(column.definition)) {
        label = {
          value: "label_suggested",
          disabled: true,
          inputDisplay: <b>Suggested</b>,
        };
      } else if (
        !isColumnSuggested(column.definition) &&
        prevColumn &&
        isColumnSuggested(prevColumn.definition)
      ) {
        label = {
          value: "label_other",
          disabled: true,
          inputDisplay: <b>Other</b>,
        };
      }

      const option = {
        value: column.id,
        inputDisplay: (
          <EuiFlexGroup
            alignItems="center"
            gutterSize="s"
            responsive={false}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <EuiToken iconType={COLUMN_KIND_MAP[column.definition.kind].icon} />
            <span>{column.name}</span>
          </EuiFlexGroup>
        ),
      };

      return label ? [label, option] : [option];
    });

  const handleChange = (newColumnId: string) => {
    if (value === newColumnId) {
      onChange(undefined);
    } else {
      onChange(newColumnId);
    }
  };

  // Render the input display for the select option when in read-only mode
  if (readOnly) {
    const selectedOption = options.find((option) => option.value === value);
    return <>{selectedOption?.inputDisplay ?? null}</>;
  }

  return (
    <EuiSuperSelect
      popoverProps={{ style: { width: "100%" } }}
      compressed
      fullWidth
      options={options.length > 0 ? options : [noItemsOption]}
      valueOfSelected={value}
      onChange={handleChange}
      placeholder="None"
      disabled={disabled}
    />
  );
};
